import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { BrandService } from '../../core/services/brand.service';
import { DeviceModelService } from '../../core/services/device-model.service';
import { MaterialService } from '../../core/services/material.service';
import { BrandItem } from '../../models/brand.interface';
import { DeviceModelItem } from '../../models/device-model.interface';
import { MaterialItem } from '../../models/material.interface';

@Component({
  selector: 'app-options-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SidebarComponent, NavbarComponent],
  template: `
    <app-sidebar></app-sidebar>
    <app-navbar
      pageTitle="Models & Materials"
      pageSubtitle="Manage brands, device models, case materials & stock sheets"
    ></app-navbar>

    <div class="page-wrapper">
      <main class="main-content animate-in">

      <!-- TABS NAVIGATION -->
      <div class="tabs-bar glass-card">
        <button
          [class.active]="activeTab === 'brands'"
          (click)="activeTab = 'brands'"
          class="tab-btn"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
          </svg>
          Brands
          <span class="tab-count">{{ brands.length }}</span>
        </button>

        <button
          [class.active]="activeTab === 'models'"
          (click)="activeTab = 'models'"
          class="tab-btn"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
            <line x1="12" y1="18" x2="12.01" y2="18"/>
          </svg>
          Device Models
          <span class="tab-count">{{ deviceModels.length }}</span>
        </button>

        <button
          [class.active]="activeTab === 'materials'"
          (click)="activeTab = 'materials'"
          class="tab-btn"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
          </svg>
          case type
          <span class="tab-count">{{ materials.length }}</span>
        </button>

        <button
          [class.active]="activeTab === 'bulk'"
          (click)="activeTab = 'bulk'"
          class="tab-btn tab-btn-sparkle"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          ⚡ Bulk Stock Upload
          <span class="tab-badge-excel">Excel / Multi-Sheet</span>
        </button>
      </div>

      <!-- TAB 1: BRANDS CRUD -->
      <div *ngIf="activeTab === 'brands'" class="tab-content">
        <div class="card-grid">
          <div class="form-card glass-card">
            <div class="form-card-header">
              <div class="icon-box icon-box-gold icon-box-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                </svg>
              </div>
              <h3 class="font-serif title-gold" style="font-size: 15px;">Add Brand / Manufacturer</h3>
            </div>
            <form (ngSubmit)="onAddBrand()" class="opt-form">
              <div class="form-group">
                <label class="form-label">Brand Name *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  [(ngModel)]="newBrand.name" 
                  name="brandName" 
                  placeholder="e.g. Apple, Samsung, Google, OnePlus" 
                  required 
                />
              </div>

              <div class="form-group">
                <label class="form-label">Description / Series Info</label>
                <textarea 
                  class="form-control" 
                  rows="3" 
                  [(ngModel)]="newBrand.description" 
                  name="brandDesc" 
                  placeholder="iPhone 16, 15, 14 Pro Max flagship models"
                ></textarea>
              </div>

              <button type="submit" class="btn-gold" [disabled]="addingBrand">
                <span *ngIf="!addingBrand">Save Brand Master</span>
                <span *ngIf="addingBrand">Saving...</span>
              </button>
            </form>
          </div>

          <!-- BRANDS LIST TABLE -->
          <div class="table-card glass-card">
            <h3 class="font-serif title-gold" style="margin-bottom: 16px;">Registered Brands / Manufacturers</h3>
            <table class="custom-table">
              <thead>
                <tr>
                  <th>Brand Name</th>
                  <th>Series & Description</th>
                  <th style="text-align: right;">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let brand of brands">
                  <td><strong>{{ brand.name }}</strong></td>
                  <td style="font-size: 12px; color: var(--text-secondary);">{{ brand.description || 'Global manufacturer' }}</td>
                  <td style="text-align: right;">
                  <button (click)="onDeleteBrand(brand)" class="btn-danger btn-icon" title="Delete brand">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    </svg>
                  </button>
                  </td>
                </tr>
                <tr *ngIf="brands.length === 0">
                  <td colspan="3" class="text-center" style="padding: 24px;">No brands registered yet.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- TAB 2: DEVICE MODELS CRUD -->
      <div *ngIf="activeTab === 'models'" class="tab-content">
        <div class="card-grid">
          <!-- CREATE DEVICE MODEL FORM -->
          <div class="form-card glass-card">
            <div class="form-card-header">
              <div class="icon-box icon-box-blue icon-box-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                  <line x1="12" y1="18" x2="12.01" y2="18"/>
                </svg>
              </div>
              <h3 class="font-serif title-gold" style="font-size: 15px;">Add Device Model</h3>
            </div>
            <form (ngSubmit)="onAddDeviceModel()" class="opt-form">
              <div class="form-group">
                <label class="form-label">Brand / Manufacturer *</label>
                <select class="form-control" [(ngModel)]="newModel.brand" name="modelBrandSelect" required>
                  <option *ngFor="let b of brands" [value]="b.name">{{ b.name }}</option>
                </select>
              </div>

              <div class="form-group">
                <label class="form-label">Model Name *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  [(ngModel)]="newModel.name" 
                  name="modelName" 
                  placeholder="e.g. iPhone 16 Pro Max or Samsung S24 Ultra" 
                  required 
                />
              </div>

              <div class="form-group">
                <label class="form-label">Availability Status</label>
                <select class="form-control" [(ngModel)]="newModel.availability" name="modelAvailSelect">
                  <option value="AVL">Available (AVL)</option>
                  <option value="OOS">Out of Stock (OOS)</option>
                </select>
              </div>

              <button type="submit" class="btn-gold" [disabled]="addingModel">
                <span *ngIf="!addingModel">Save Device Model</span>
                <span *ngIf="addingModel">Saving...</span>
              </button>
            </form>
          </div>

          <!-- DEVICE MODELS LIST TABLE -->
          <div class="table-card glass-card">
            <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px;">
              <h3 class="font-serif title-gold" style="margin:0;">Registered Models ({{ deviceModels.length }})</h3>
              <button class="btn-secondary" style="font-size:12px; padding:6px 12px;" (click)="activeTab = 'bulk'">
                ⚡ Bulk Upload Stock
              </button>
            </div>
            <div style="max-height: 520px; overflow-y: auto;">
              <table class="custom-table">
                <thead>
                  <tr>
                    <th>Brand</th>
                    <th>Model Name</th>
                    <th>Availability</th>
                    <th style="text-align: right;">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let model of deviceModels">
                    <td><span class="badge badge-gold">{{ model.brand || 'Apple' }}</span></td>
                    <td><strong>{{ model.name }}</strong></td>
                    <td>
                      <span class="stock-badge" [class.badge-avl]="model.inStock !== false" [class.badge-oos]="model.inStock === false">
                        {{ model.availability || (model.inStock !== false ? 'AVL' : 'OOS') }}
                      </span>
                    </td>
                    <td style="text-align: right;">
                      <button (click)="onDeleteDeviceModel(model)" class="btn-danger btn-icon" title="Delete device model">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                  <tr *ngIf="deviceModels.length === 0">
                    <td colspan="5" class="text-center" style="padding: 24px;">No device models registered yet.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- TAB 3: MATERIALS CRUD -->
      <div *ngIf="activeTab === 'materials'" class="tab-content">
        <div class="card-grid">
          <!-- CREATE MATERIAL FORM -->
          <div class="form-card glass-card">
            <div class="form-card-header">
              <div class="icon-box icon-box-purple icon-box-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
                </svg>
              </div>
              <h3 class="font-serif title-gold" style="font-size: 15px;">Add Case Material</h3>
            </div>
            <form (ngSubmit)="onAddMaterial()" class="opt-form">
              <div class="form-group">
                <label class="form-label">Material Name *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  [(ngModel)]="newMaterial.name" 
                  name="matName" 
                  placeholder="e.g. 9H Tempered Glass or Soft Silicone Flex" 
                  required 
                />
              </div>

              <div class="form-group">
                <label class="form-label">Description / Specs</label>
                <textarea 
                  class="form-control" 
                  rows="3" 
                  [(ngModel)]="newMaterial.description" 
                  name="matDesc" 
                  placeholder="Anti-scratch 9H glass with premium shine & drop buffer"
                ></textarea>
              </div>

              <button type="submit" class="btn-gold" [disabled]="addingMaterial">
                <span *ngIf="!addingMaterial">Save Material Master</span>
                <span *ngIf="addingMaterial">Saving...</span>
              </button>
            </form>
          </div>

          <!-- MATERIALS LIST TABLE -->
          <div class="table-card glass-card">
            <h3 class="font-serif title-gold" style="margin-bottom: 16px;">Registered Case Materials</h3>
            <table class="custom-table">
              <thead>
                <tr>
                  <th>Material Type</th>
                  <th>Specs & Description</th>
                  <th style="text-align: right;">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let mat of materials">
                  <td><strong>{{ mat.name }}</strong></td>
                  <td style="font-size: 12px; color: var(--text-secondary);">{{ mat.description || 'Standard luxury material' }}</td>
                  <td style="text-align: right;">
                    <button (click)="onDeleteMaterial(mat)" class="btn-danger btn-icon" title="Delete material">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                      </svg>
                    </button>
                  </td>
                </tr>
                <tr *ngIf="materials.length === 0">
                  <td colspan="3" class="text-center" style="padding: 24px;">No case materials registered yet.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ── TAB 4: BULK STOCK & MODELS UPLOAD (EXCEL) ────────────────── -->
      <div *ngIf="activeTab === 'bulk'" class="tab-content animate-in">
        <div class="bulk-stock-container">

          <!-- Header info banner -->
          <div class="bulk-header-banner glass-card">
            <div class="bulk-banner-left">
              <div class="icon-box icon-box-gold">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              <div>
                <h2 class="font-serif title-gold" style="margin:0 0 4px 0; font-size:18px;">
                  Bulk Upload Device Models, Brands & Stock
                </h2>
                <p class="sub-text" style="margin:0;">
                  Upload multi-sheet Excel files (like <code>GLASS CASE STOCK_MYXTUR.xlsx</code>). Automatically creates brands, registers models, and synchronizes availability (AVL / OOS).
                </p>
              </div>
            </div>
            <button class="btn-secondary" (click)="downloadStockTemplate()">
              ⬇ Download Sample Template
            </button>
          </div>

          <!-- STEP 1: File Upload Box -->
          <div *ngIf="!parsedStockData && !stockImportResult" class="glass-card" style="padding: 24px;">
            <!-- Material Selection Bar -->
            <div class="material-config-bar">
              <label class="form-label" style="margin-bottom:6px;">
                🎯 Target Case Material for this Stock Upload:
              </label>
              <div style="display:flex; gap:12px; align-items:center; flex-wrap:wrap;">
                <select class="form-control" style="max-width: 280px;" [(ngModel)]="selectedMaterialForImport">
                  <option value="9H Tempered Glass">9H Tempered Glass (Glass Case)</option>
                  <option value="Hard & Glass Armor">Hard & Glass Armor</option>
                  <option value="Tough TPU Armor">Tough TPU Armor</option>
                  <option value="Vegan Leather">Vegan Leather</option>
                  <option *ngFor="let m of materials" [value]="m.name">{{ m.name }}</option>
                  <option value="__custom__">+ Enter Custom Material...</option>
                </select>
                <input
                  *ngIf="selectedMaterialForImport === '__custom__'"
                  type="text"
                  class="form-control"
                  style="max-width:240px;"
                  [(ngModel)]="customMaterialName"
                  placeholder="e.g. Carbon Fiber Kevlar"
                />
                <span class="sub-text" style="font-size:12px;">This material tag will be associated with imported models.</span>
              </div>
            </div>

            <!-- Drag & Drop Zone -->
            <div
              class="stock-dropzone"
              [class.drag-over]="isStockDragOver"
              (dragover)="$event.preventDefault(); isStockDragOver = true"
              (dragleave)="isStockDragOver = false"
              (drop)="onStockDrop($event)"
              (click)="stockFileInput.click()"
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#C9A96E" stroke-width="1.8" style="margin-bottom:12px">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="12" y1="18" x2="12" y2="12"/>
                <polyline points="9 15 12 12 15 15"/>
              </svg>
              <h4 style="margin:0 0 6px 0; color:var(--text-primary); font-size:15px;">
                {{ selectedStockFileName ? selectedStockFileName : 'Drag & drop your Stock Excel workbook here' }}
              </h4>
              <p class="sub-text" style="margin:0 0 14px 0;">Supports .xlsx, .xls, .csv with multi-sheet brand tables (Apple, Samsung, OnePlus, etc.)</p>
              <input
                #stockFileInput
                type="file"
                accept=".xlsx,.xls,.csv"
                style="display:none"
                (change)="onStockFileSelected($event)"
              />
              <button type="button" class="btn-gold" style="pointer-events:none;">
                Browse Files
              </button>
            </div>

            <!-- Error message if any -->
            <div *ngIf="parseStockError" class="bulk-error-banner">
              ⚠️ {{ parseStockError }}
            </div>

            <!-- Parsing Spinner -->
            <div *ngIf="parsingStock" class="parsing-spinner-box">
              <div class="loading-spinner"></div>
              <span>Reading and analyzing workbook sheets...</span>
            </div>
          </div>

          <!-- STEP 2: Analysis & Preview Table -->
          <div *ngIf="parsedStockData && !stockImportResult" class="glass-card" style="padding: 24px;">
            <!-- Metrics Summary Cards -->
            <div class="stock-stats-row">
              <div class="stock-stat-box">
                <span class="stock-stat-label">Total Models</span>
                <span class="stock-stat-val text-gold">{{ parsedStockData.totalModels }}</span>
              </div>
              <div class="stock-stat-box">
                <span class="stock-stat-label">Brands Detected</span>
                <span class="stock-stat-val">{{ parsedStockData.brands.length }}</span>
              </div>
              <div class="stock-stat-box">
                <span class="stock-stat-label">In Stock (AVL)</span>
                <span class="stock-stat-val text-green">{{ getAvailableCount() }}</span>
              </div>
              <div class="stock-stat-box">
                <span class="stock-stat-label">Out of Stock (OOS)</span>
                <span class="stock-stat-val text-danger">{{ getOosCount() }}</span>
              </div>
              <div class="stock-stat-box">
                <span class="stock-stat-label">Material</span>
                <span class="stock-stat-val text-purple" style="font-size:14px;">{{ getEffectiveMaterial() }}</span>
              </div>
            </div>

            <!-- Brand Filters Row -->
            <div class="brand-filter-chips">
              <button
                class="brand-chip"
                [class.active]="filterStockBrand === 'all'"
                (click)="setBrandFilter('all')"
              >
                All Brands ({{ parsedStockData.totalModels }})
              </button>
              <button
                *ngFor="let b of parsedStockData.brands"
                class="brand-chip"
                [class.active]="filterStockBrand === b.name"
                (click)="setBrandFilter(b.name)"
              >
                {{ b.name }}
                <span class="chip-count">{{ b.count }}</span>
              </button>
            </div>

            <!-- Search & Options toolbar -->
            <div class="stock-preview-toolbar">
              <div class="search-wrap">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  class="form-control search-input"
                  [(ngModel)]="searchStockQuery"
                  placeholder="Filter models by name (e.g. Pro Max, Ultra, 5G)..."
                />
              </div>

              <!-- Options -->
              <div class="import-options">
                <label class="checkbox-label" title="Will create missing Brand master records">
                  <input type="checkbox" [(ngModel)]="createBrandsChecked" />
                  <span>Auto-create Brands</span>
                </label>
                <label class="checkbox-label" title="Will register Material master if not present">
                  <input type="checkbox" [(ngModel)]="createMaterialChecked" />
                  <span>Auto-create Material</span>
                </label>
                <label class="checkbox-label" title="Will update availability and stock on existing models">
                  <input type="checkbox" [(ngModel)]="updateExistingChecked" />
                  <span>Update Existing Models</span>
                </label>
              </div>
            </div>

            <!-- Preview Data Table -->
            <div class="stock-preview-table-wrap">
              <table class="custom-table">
                <thead>
                  <tr>
                    <th style="width: 50px;">#</th>
                    <th>Sheet</th>
                    <th>Brand</th>
                    <th>Model Name</th>
                    <th>Availability</th>
                    <th>Stock Status</th>
                    <th>Target Material</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let m of getFilteredStockModels(); let i = index">
                    <td class="sub-text">{{ i + 1 }}</td>
                    <td class="sub-text">{{ m.sheet }}</td>
                    <td><span class="badge badge-gold">{{ m.brand }}</span></td>
                    <td><strong>{{ m.name }}</strong></td>
                    <td>
                      <span class="stock-badge" [class.badge-avl]="m.inStock" [class.badge-oos]="!m.inStock">
                        {{ m.availability }}
                      </span>
                    </td>
                    <td>
                      <span class="sub-text" [style.color]="m.inStock ? '#34d399' : '#f87171'">
                        {{ m.inStock ? '● Available' : '○ Out of Stock' }}
                      </span>
                    </td>
                    <td><span class="badge badge-purple" style="font-size:11px;">{{ m.material || getEffectiveMaterial() }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Actions Bar -->
            <div class="stock-actions-bar">
              <button class="btn-secondary" (click)="resetStockImport()">
                ← Choose Another File
              </button>
              <button
                class="btn-gold btn-lg"
                [disabled]="importingStock"
                (click)="startStockImport()"
              >
                <span *ngIf="!importingStock">🚀 Import {{ getFilteredStockModels().length }} {{ filterStockMaterial === 'all' ? 'All' : filterStockMaterial }} Models</span>
                <span *ngIf="importingStock">Importing into Catalog...</span>
              </button>
            </div>
          </div>

          <!-- STEP 3: Done Result -->
          <div *ngIf="stockImportResult" class="glass-card done-card animate-in">
            <div class="done-icon-box">✓</div>
            <h2 class="font-serif title-gold" style="font-size:22px; margin:0 0 6px 0;">Stock Import Successful!</h2>
            <p class="sub-text" style="margin:0 0 20px 0;">
              All device models, brands, and stock availability statuses have been synchronized into MongoDB.
            </p>

            <div class="result-stats-grid">
              <div class="result-stat-box">
                <span class="result-stat-num text-green">{{ stockImportResult.createdCount }}</span>
                <span class="sub-text">New Models Created</span>
              </div>
              <div class="result-stat-box">
                <span class="result-stat-num text-gold">{{ stockImportResult.updatedCount }}</span>
                <span class="sub-text">Models Updated</span>
              </div>
              <div class="result-stat-box">
                <span class="result-stat-num">{{ stockImportResult.brandsCount }}</span>
                <span class="sub-text">Brands Synchronized</span>
              </div>
              <div class="result-stat-box">
                <span class="result-stat-num text-purple">{{ stockImportResult.material || 'Standard' }}</span>
                <span class="sub-text">Material Linked</span>
              </div>
            </div>

            <div style="display:flex; gap:14px; justify-content:center; margin-top:24px;">
              <button class="btn-gold" (click)="activeTab = 'models'; loadDeviceModels(); loadBrands();">
                View Device Models ({{ deviceModels.length }}) →
              </button>
              <button class="btn-secondary" (click)="resetStockImport()">
                Upload Another Sheet
              </button>
            </div>
          </div>

        </div>
      </div>

      </main>
    </div>
  `,
  styles: [`
    .main-content {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .tabs-bar {
      display: flex;
      gap: 6px;
      padding: 12px 16px;
      flex-wrap: wrap;
    }

    .tab-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 9px 18px;
      border-radius: var(--radius-full);
      background: transparent;
      border: 1px solid transparent;
      color: var(--text-secondary);
      font-family: var(--font-sans);
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all var(--transition-fast);
    }
    .tab-btn:hover {
      color: var(--text-primary);
      background: rgba(255, 255, 255, 0.04);
    }
    .tab-btn.active {
      background: rgba(201, 169, 110, 0.12);
      border-color: rgba(201, 169, 110, 0.35);
      color: var(--accent-gold);
    }

    .tab-btn-sparkle.active {
      border-color: rgba(212, 175, 55, 0.6);
      box-shadow: 0 0 16px rgba(212, 175, 55, 0.2);
    }

    .tab-badge-excel {
      background: rgba(212, 175, 55, 0.18);
      color: var(--accent-gold);
      font-size: 10px;
      padding: 2px 7px;
      border-radius: 6px;
      border: 1px solid rgba(212, 175, 55, 0.35);
    }

    .tab-count {
      font-size: 11px;
      background: rgba(255, 255, 255, 0.08);
      padding: 1px 6px;
      border-radius: 10px;
      color: var(--text-muted);
    }
    .tab-btn.active .tab-count {
      background: rgba(201, 169, 110, 0.2);
      color: var(--accent-gold);
    }

    .card-grid {
      display: grid;
      grid-template-columns: 360px 1fr;
      gap: 24px;
      align-items: start;
    }

    .form-card, .table-card {
      padding: 24px;
    }

    .form-card-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 20px;
      padding-bottom: 14px;
      border-bottom: 1px solid var(--border-subtle);
    }

    .opt-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .custom-table {
      width: 100%;
      border-collapse: collapse;
      text-align: left;
      font-size: 13px;
    }

    .custom-table th {
      padding: 10px 14px;
      color: var(--text-muted);
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      border-bottom: 1px solid var(--border-subtle);
    }

    .custom-table td {
      padding: 12px 14px;
      color: var(--text-primary);
      border-bottom: 1px solid var(--border-subtle);
    }

    .btn-icon {
      padding: 6px;
      border-radius: 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }

    .stock-badge {
      display: inline-block;
      font-size: 11px;
      font-weight: 700;
      padding: 2px 8px;
      border-radius: 4px;
      text-transform: uppercase;
    }
    .badge-avl {
      background: rgba(52, 211, 153, 0.12);
      color: #34d399;
      border: 1px solid rgba(52, 211, 153, 0.3);
    }
    .badge-oos {
      background: rgba(248, 113, 113, 0.12);
      color: #f87171;
      border: 1px solid rgba(248, 113, 113, 0.3);
    }

    /* ── BULK STOCK STYLES ────────────────────────────────────────────── */
    .bulk-stock-container {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .bulk-header-banner {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px;
      flex-wrap: wrap;
      gap: 16px;
    }
    .bulk-banner-left {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .material-config-bar {
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid var(--border-subtle);
      border-radius: var(--radius-md);
      padding: 16px 20px;
      margin-bottom: 20px;
    }

    .stock-dropzone {
      border: 2px dashed rgba(201, 169, 110, 0.3);
      border-radius: var(--radius-lg);
      padding: 44px 24px;
      text-align: center;
      cursor: pointer;
      background: rgba(201, 169, 110, 0.02);
      transition: all var(--transition-fast);
    }
    .stock-dropzone:hover, .stock-dropzone.drag-over {
      border-color: var(--accent-gold);
      background: rgba(201, 169, 110, 0.06);
    }

    .bulk-error-banner {
      margin-top: 16px;
      padding: 12px 18px;
      background: rgba(248, 113, 113, 0.1);
      border: 1px solid rgba(248, 113, 113, 0.3);
      border-radius: var(--radius-md);
      color: var(--danger);
      font-size: 13px;
    }

    .parsing-spinner-box {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      margin-top: 20px;
      color: var(--accent-gold);
      font-size: 13.5px;
    }

    .stock-stats-row {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 14px;
      margin-bottom: 20px;
    }
    .stock-stat-box {
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid var(--border-subtle);
      border-radius: var(--radius-md);
      padding: 14px 18px;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .stock-stat-label {
      font-size: 11px;
      color: var(--text-muted);
      text-transform: uppercase;
      font-weight: 700;
      letter-spacing: 0.05em;
    }
    .stock-stat-val {
      font-size: 22px;
      font-weight: 800;
      color: var(--text-primary);
    }

    .brand-filter-chips {
      display: flex;
      gap: 8px;
      overflow-x: auto;
      padding-bottom: 8px;
      margin-bottom: 16px;
    }
    .brand-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 14px;
      border-radius: var(--radius-full);
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid var(--border-subtle);
      color: var(--text-secondary);
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
      transition: all var(--transition-fast);
    }
    .brand-chip:hover {
      color: var(--text-primary);
      border-color: rgba(255, 255, 255, 0.2);
    }
    .brand-chip.active {
      background: rgba(201, 169, 110, 0.15);
      border-color: var(--accent-gold);
      color: var(--accent-gold);
    }
    .chip-count {
      font-size: 10px;
      background: rgba(0, 0, 0, 0.3);
      padding: 1px 5px;
      border-radius: 8px;
    }

    .stock-preview-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      margin-bottom: 16px;
      flex-wrap: wrap;
    }
    .search-wrap {
      position: relative;
      flex: 1;
      min-width: 260px;
      display: flex;
      align-items: center;
    }
    .search-wrap svg {
      position: absolute;
      left: 14px;
      color: var(--text-muted);
      pointer-events: none;
    }
    .search-input {
      padding-left: 38px;
      width: 100%;
    }

    .import-options {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
    }
    .checkbox-label {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 12.5px;
      color: var(--text-secondary);
      cursor: pointer;
    }

    .stock-preview-table-wrap {
      max-height: 420px;
      overflow-y: auto;
      border: 1px solid var(--border-subtle);
      border-radius: var(--radius-md);
      margin-bottom: 20px;
    }

    .stock-actions-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 14px;
      border-top: 1px solid var(--border-subtle);
      flex-wrap: wrap;
      gap: 14px;
    }

    .done-card {
      padding: 48px 24px;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .done-icon-box {
      width: 68px;
      height: 68px;
      border-radius: 50%;
      background: rgba(52, 211, 153, 0.15);
      border: 2px solid #34d399;
      color: #34d399;
      font-size: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 18px;
    }
    .result-stats-grid {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
      justify-content: center;
      margin-bottom: 8px;
    }
    .result-stat-box {
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid var(--border-subtle);
      border-radius: var(--radius-md);
      padding: 14px 24px;
      min-width: 130px;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .result-stat-num {
      font-size: 24px;
      font-weight: 800;
    }

    @media (max-width: 992px) {
      .card-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class OptionsListComponent implements OnInit {
  activeTab: 'brands' | 'models' | 'materials' | 'bulk' = 'brands';

  brands: BrandItem[] = [];
  deviceModels: DeviceModelItem[] = [];
  materials: MaterialItem[] = [];

  addingBrand = false;
  newBrand: { name: string; description: string } = { name: '', description: '' };

  addingModel = false;
  newModel: { name: string; brand: string; availability: string } = { name: '', brand: 'Apple', availability: 'AVL' };

  addingMaterial = false;
  newMaterial: { name: string; description: string } = { name: '', description: '' };

  // ── Bulk Stock Upload State ──────────────────────
  selectedStockFile: File | null = null;
  selectedStockFileName = '';
  isStockDragOver = false;
  parsingStock = false;
  parseStockError = '';

  parsedStockData: {
    fileName: string;
    sheetNames: string[];
    activeSheet?: string;
    totalModels: number;
    materials?: Array<{ name: string; count: number; avl: number; oos: number }>;
    brands: Array<{ name: string; count: number; avl: number; oos: number }>;
    detectedMaterial?: string;
    models: Array<{ rowNumber: number; sheet: string; material?: string; brand: string; name: string; availability: string; inStock: boolean }>;
  } | null = null;

  selectedMaterialForImport = '9H Tempered Glass';
  customMaterialName = '';
  filterStockBrand = 'all';
  filterStockMaterial = 'all';
  searchStockQuery = '';

  createBrandsChecked = true;
  createMaterialChecked = true;
  updateExistingChecked = true;

  importingStock = false;
  stockImportResult: any = null;

  constructor(
    private brandService: BrandService,
    private deviceModelService: DeviceModelService,
    private materialService: MaterialService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['tab'] && ['brands', 'models', 'materials', 'bulk'].includes(params['tab'])) {
        this.activeTab = params['tab'];
      }
    });

    this.loadBrands();
    this.loadDeviceModels();
    this.loadMaterials();
  }

  loadBrands(): void {
    this.brandService.getBrands().subscribe({
      next: (res) => {
        this.brands = res;
        if (res.length > 0 && !this.newModel.brand) {
          this.newModel.brand = res[0].name;
        }
      },
      error: (err) => console.error(err)
    });
  }

  loadDeviceModels(): void {
    this.deviceModelService.getDeviceModels().subscribe({
      next: (res) => (this.deviceModels = res),
      error: (err) => console.error(err)
    });
  }

  loadMaterials(): void {
    this.materialService.getMaterials().subscribe({
      next: (res) => (this.materials = res),
      error: (err) => console.error(err)
    });
  }

  onAddBrand(): void {
    if (!this.newBrand.name.trim()) return;
    this.addingBrand = true;
    this.brandService.createBrand(this.newBrand).subscribe({
      next: (saved) => {
        this.addingBrand = false;
        this.brands.push(saved);
        this.newBrand = { name: '', description: '' };
      },
      error: (err) => {
        this.addingBrand = false;
        alert('Failed to add brand: ' + err.message);
      }
    });
  }

  onDeleteBrand(brand: BrandItem): void {
    if (!brand._id) return;
    if (confirm(`Delete brand "${brand.name}"?`)) {
      this.brandService.deleteBrand(brand._id).subscribe({
        next: () => {
          this.brands = this.brands.filter((b) => b._id !== brand._id);
        },
        error: (err) => alert('Failed to delete brand: ' + err.message)
      });
    }
  }

  onAddDeviceModel(): void {
    if (!this.newModel.name.trim()) return;
    this.addingModel = true;
    this.deviceModelService.createDeviceModel(this.newModel).subscribe({
      next: (saved) => {
        this.addingModel = false;
        this.deviceModels.push(saved);
        this.newModel.name = '';
      },
      error: (err) => {
        this.addingModel = false;
        alert('Failed to add device model: ' + err.message);
      }
    });
  }

  onDeleteDeviceModel(model: DeviceModelItem): void {
    if (!model._id) return;
    if (confirm(`Delete device model "${model.name}"?`)) {
      this.deviceModelService.deleteDeviceModel(model._id).subscribe({
        next: () => {
          this.deviceModels = this.deviceModels.filter((m) => m._id !== model._id);
        },
        error: (err) => alert('Failed to delete device model: ' + err.message)
      });
    }
  }

  onAddMaterial(): void {
    if (!this.newMaterial.name.trim()) return;
    this.addingMaterial = true;
    this.materialService.createMaterial(this.newMaterial).subscribe({
      next: (saved) => {
        this.addingMaterial = false;
        this.materials.push(saved);
        this.newMaterial = { name: '', description: '' };
      },
      error: (err) => {
        this.addingMaterial = false;
        alert('Failed to add material: ' + err.message);
      }
    });
  }

  onDeleteMaterial(mat: MaterialItem): void {
    if (!mat._id) return;
    if (confirm(`Delete material "${mat.name}"?`)) {
      this.materialService.deleteMaterial(mat._id).subscribe({
        next: () => {
          this.materials = this.materials.filter((m) => m._id !== mat._id);
        },
        error: (err) => alert('Failed to delete material: ' + err.message)
      });
    }
  }

  // ── Bulk Stock Upload Methods ────────────────────
  onStockFileSelected(event: any): void {
    const file = event.target.files?.[0];
    if (file) {
      this.handleStockFile(file);
    }
  }

  onStockDrop(event: DragEvent): void {
    event.preventDefault();
    this.isStockDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.handleStockFile(file);
    }
  }

  handleStockFile(file: File): void {
    this.selectedStockFile = file;
    this.selectedStockFileName = file.name;
    this.parseStockError = '';
    this.parsedStockData = null;
    this.stockImportResult = null;
    this.parsingStock = true;

    this.deviceModelService.parseStockExcel(file).subscribe({
      next: (res) => {
        this.parsingStock = false;
        this.parsedStockData = res;
        if (res.detectedMaterial) {
          this.selectedMaterialForImport = res.detectedMaterial;
        }
        this.filterStockBrand = 'all';
      },
      error: (err) => {
        this.parsingStock = false;
        this.parseStockError = err.error?.message || err.message || 'Failed to read Excel workbook';
      }
    });
  }

  getEffectiveMaterial(): string {
    if (this.selectedMaterialForImport === '__custom__') {
      return this.customMaterialName.trim() || 'Glass Case';
    }
    return this.selectedMaterialForImport;
  }

  setBrandFilter(b: string): void {
    this.filterStockBrand = b;
  }

  setMaterialFilter(m: string): void {
    this.filterStockMaterial = m;
  }

  getFilteredStockModels(): any[] {
    if (!this.parsedStockData?.models) return [];
    let list = this.parsedStockData.models;

    if (this.filterStockMaterial !== 'all') {
      list = list.filter(m => (m.material || '').toLowerCase() === this.filterStockMaterial.toLowerCase());
    }

    if (this.filterStockBrand !== 'all') {
      list = list.filter(m => m.brand === this.filterStockBrand);
    }

    if (this.searchStockQuery.trim()) {
      const q = this.searchStockQuery.toLowerCase().trim();
      list = list.filter(m =>
        m.name.toLowerCase().includes(q) ||
        m.brand.toLowerCase().includes(q) ||
        (m.material && m.material.toLowerCase().includes(q))
      );
    }

    return list;
  }

  getAvailableCount(): number {
    return this.getFilteredStockModels().filter(m => m.inStock).length;
  }

  getOosCount(): number {
    return this.getFilteredStockModels().filter(m => !m.inStock).length;
  }

  startStockImport(): void {
    if (!this.parsedStockData?.models || this.importingStock) return;

    this.importingStock = true;
    const modelsToImport = this.filterStockMaterial === 'all'
      ? this.parsedStockData.models
      : this.parsedStockData.models.filter((m: any) => (m.material || '').toLowerCase() === this.filterStockMaterial.toLowerCase());

    const targetMaterial = this.filterStockMaterial !== 'all'
      ? this.filterStockMaterial
      : (this.parsedStockData.materials && this.parsedStockData.materials.length === 1 ? this.parsedStockData.materials[0].name : this.getEffectiveMaterial());

    const payload = {
      models: modelsToImport,
      material: targetMaterial,
      createBrands: this.createBrandsChecked,
      createMaterial: this.createMaterialChecked,
      updateExisting: this.updateExistingChecked
    };

    this.deviceModelService.bulkImport(payload).subscribe({
      next: (res) => {
        this.importingStock = false;
        this.stockImportResult = res;
        this.loadDeviceModels();
        this.loadBrands();
        this.loadMaterials();
      },
      error: (err) => {
        this.importingStock = false;
        alert('Import failed: ' + (err.error?.message || err.message));
      }
    });
  }

  resetStockImport(): void {
    this.selectedStockFile = null;
    this.selectedStockFileName = '';
    this.parsedStockData = null;
    this.stockImportResult = null;
    this.parseStockError = '';
    this.searchStockQuery = '';
    this.filterStockBrand = 'all';
    this.filterStockMaterial = 'all';
  }

  downloadStockTemplate(): void {
    this.deviceModelService.downloadStockTemplate().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'shell_muze_stock_models_template.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => alert('Failed to download template: ' + err.message)
    });
  }
}
